// Zitadel's hosted login, through the authorization code flow with PKCE.
//
// Ported from coachai's web/src/auth/oidc.ts. oidc-client-ts does the
// protocol: discovery, the code challenge, the redirect, the token exchange,
// and renewal through the refresh token `offline_access` earns. What this file
// adds is the configuration and the one rule the console cares about: the
// access token is the credential the API sees, and it is handed to the
// transport rather than decoded here — the server re-resolves the caller on
// every request.
//
// # What the Zitadel application must register
//
// A user-agent (public, PKCE) app with JWT access tokens, the authorization
// code and refresh token grants, redirect URI `<origin>/auth/callback` and
// post-logout URI `<origin>`. The server's ZITADEL_AUDIENCE is the app's
// client id: Zitadel puts the client id in a JWT access token's `aud`.

import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

const issuer = import.meta.env.VITE_ZITADEL_ISSUER as string | undefined;
const clientID = import.meta.env.VITE_ZITADEL_CLIENT_ID as string | undefined;

/** Where Zitadel sends the browser back; the SPA fallback serves index.html for it. */
export const CALLBACK_PATH = '/auth/callback';

/** Whether this build was given a Zitadel to talk to. */
export function oidcConfigured(): boolean {
  return !!issuer && !!clientID;
}

let manager: UserManager | null = null;

export function oidc(): UserManager {
  if (!manager) {
    if (!issuer || !clientID) {
      throw new Error('VITE_ZITADEL_ISSUER and VITE_ZITADEL_CLIENT_ID are required for the Zitadel build');
    }
    manager = new UserManager({
      authority: issuer,
      client_id: clientID,
      redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
      post_logout_redirect_uri: window.location.origin,
      response_type: 'code',
      scope: 'openid profile email offline_access',
      automaticSilentRenew: true,
      // localStorage, like the credential in session.tsx: a console tab
      // reopened tomorrow resumes rather than signing in again.
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    });
  }
  return manager;
}
